import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { ArrowRight, Building, Shield, Wrench, Layers, Ruler, FileCheck } from "lucide-react";

const COPY = {
  de: {
    eyebrow: "Leistungen · 02",
    h1a: "Sanierung &",
    h1b_em: "Revitalisierung.",
    lede: "Behutsamer Umgang mit Bestand. Wir bewahren Substanz, ertüchtigen Tragwerk und Hülle und führen Wohnungen, Häuser und Liegenschaften zurück in einen langfristig nutzbaren Zustand.",
    intro_title: "Bestand als Aufgabe.",
    intro_body: "Sanierung erfordert eine ehrliche Bestandsaufnahme: Tragwerk, Bauphysik, Haustechnik, Brandschutz, Schadstoffe. Auf dieser Grundlage entwickeln wir ein realistisches Sanierungskonzept — mit klarem Eingriff dort, wo es nötig ist, und Zurückhaltung dort, wo Bestand erhalten bleiben soll.",
    scope_title: "Leistungsumfang.",
    items: [
      { icon: Building, no: "01", title: "Bestandserfassung", text: "Tragwerksprüfung, Bauphysik-Check, Schadstoff- und Feuchtemessung, Haustechnik-Status." },
      { icon: Ruler, no: "02", title: "Sanierungskonzept", text: "Variantenstudie, Kostenschätzung, Genehmigungsstrategie, Zeitplan." },
      { icon: Layers, no: "03", title: "Tragwerk & Hülle", text: "Statische Ertüchtigung, Fassadensanierung, Wärmebrückenkonzept, Fenster und Dach." },
      { icon: Wrench, no: "04", title: "Haustechnik", text: "Erneuerung Heizung / Lüftung / Sanitär / Elektrotechnik gemeinsam mit ECO Building Technik." },
      { icon: Shield, no: "05", title: "Brand- und Schallschutz", text: "Brandabschnitte, Fluchtwege, akustische Trennung gemäß aktuellem Stand der Technik." },
      { icon: FileCheck, no: "06", title: "Genehmigung & Abnahme", text: "Behördenweg, statische und brandschutzrechtliche Nachweise, Endabnahmen." },
    ],
    types_title: "Typen.",
    types: [
      { title: "Altbau-Wohnung", text: "Sanierung und Aufwertung einzelner Wohnungen — Grundriss, Bäder, Böden, Fenster, Haustechnik." },
      { title: "Zinshaus / Mehrparteienhaus", text: "Gesamtsanierung historischer Substanz mit optionaler Aufstockung." },
      { title: "Einfamilienhaus", text: "Energetische Sanierung, Hüllen-Ertüchtigung, Grundriss-Anpassung." },
      { title: "Mischnutzung", text: "Umwidmung von Erdgeschoss-Flächen, Aufwertung der Aufgangs- und Erschließungsbereiche." },
    ],

    ref_eyebrow: "Referenzprojekt",
    ref_title: "Bauernhaus Salzburg.",
    ref_body: "Behutsame Sanierung eines historischen Bauernhauses im Salzburger Umland. Erhalt der ortstypischen Substanz, energetische Hüllen-Sanierung, Erneuerung der Haustechnik. Wieder dauerhaft nutzbar — mit Charakter und niedrigem Betriebskostenniveau.",
    ref_cta: "Projekt ansehen",

    cta_title: "Sanierungsprojekt im Kopf?",
    cta_body: "Wir prüfen Substanz, Potenzial und Wirtschaftlichkeit in einem ersten Gespräch — unverbindlich.",
    cta_btn: "Beratung anfragen",
  },
  en: {
    eyebrow: "Services · 02",
    h1a: "Rehabilitation &",
    h1b_em: "Revitalization.",
    lede: "Careful handling of existing structures. We preserve substance, reinforce structures and envelopes, and restore apartments, houses, and properties to a long-term usable condition.",
    intro_title: "Existing Structures as a Task.",
    intro_body: "Rehabilitation requires an honest assessment of existing conditions: structural framework, building physics, building services, fire protection, pollutants. Based on this, we develop a realistic rehabilitation concept — with clear intervention where necessary, and restraint where existing elements should be preserved.",
    scope_title: "Scope of Services.",
    items: [
      { icon: Building, no: "01", title: "Existing Condition Assessment", text: "Structural inspection, building physics check, pollutant and moisture measurement, building services status." },
      { icon: Ruler, no: "02", title: "Rehabilitation Concept", text: "Feasibility study, cost estimation, approval strategy, timeline." },
      { icon: Layers, no: "03", title: "Structure & Envelope", text: "Structural reinforcement, facade rehabilitation, thermal bridge concept, windows and roof." },
      { icon: Wrench, no: "04", title: "Building Services", text: "Renewal of heating / ventilation / plumbing / electrical engineering in collaboration with ECO Building Technik." },
      { icon: Shield, no: "05", title: "Fire and Sound Protection", text: "Fire compartments, escape routes, acoustic separation according to current state-of-the-art technology." },
      { icon: FileCheck, no: "06", title: "Approval & Acceptance", text: "Permit process, structural and fire protection certifications, final inspections." },
    ],
    types_title: "Types.",
    types: [
      { title: "Period Apartment", text: "Rehabilitation and enhancement of individual apartments — floor plan, bathrooms, floors, windows, building services." },
      { title: "Apartment Building / Multi-Party House", text: "Comprehensive rehabilitation of historical substance with optional vertical extension." },
      { title: "Single-Family House", text: "Energy-efficient rehabilitation, envelope reinforcement, floor plan adaptation." },
      { title: "Mixed-Use", text: "Redevelopment of ground floor areas, enhancement of access and circulation zones." },
    ],

    ref_eyebrow: "Reference Project",
    ref_title: "Farmhouse Salzburg.",
    ref_body: "Careful rehabilitation of a historic farmhouse in the Salzburg surroundings. Preservation of local character, energy-efficient envelope rehabilitation, renewal of building services. Permanently usable again — with character and low operating costs.",
    ref_cta: "View Project",

    cta_title: "Have a Rehabilitation Project in Mind?",
    cta_body: "We assess substance, potential, and economic viability in an initial, non-binding discussion.",
    cta_btn: "Request Consultation",
  },
  ru: {
    eyebrow: "Услуги · 02",
    h1a: "Санация и",
    h1b_em: "ревитализация.",
    lede: "Бережное обращение с существующими объектами. Мы сохраняем субстанцию, усиливаем несущие конструкции и оболочку, а также приводим квартиры, дома и недвижимость в состояние, пригодное для долгосрочного использования.",
    intro_title: "Существующая застройка как задача.",
    intro_body: "Санация требует честной инвентаризации существующих условий: несущих конструкций, строительной физики, инженерных систем, противопожарной защиты, загрязняющих веществ. На этой основе мы разрабатываем реалистичную концепцию санации — с четким вмешательством там, где это необходимо, и сдержанностью там, где существующие элементы должны быть сохранены.",
    scope_title: "Объем услуг.",
    items: [
      { icon: Building, no: "01", title: "Оценка существующего состояния", text: "Проверка несущих конструкций, оценка строительной физики, измерение загрязняющих веществ и влажности, статус инженерных систем." },
      { icon: Ruler, no: "02", title: "Концепция санации", text: "Изучение вариантов, смета затрат, стратегия согласования, график." },
      { icon: Layers, no: "03", title: "Несущие конструкции и оболочка", text: "Усиление конструкций, санация фасадов, концепция тепловых мостов, окна и кровля." },
      { icon: Wrench, no: "04", title: "Инженерные системы", text: "Обновление систем отопления / вентиляции / водопровода / электротехники совместно с ECO Building Technik." },
      { icon: Shield, no: "05", title: "Противопожарная и звукоизоляционная защита", text: "Пожарные отсеки, пути эвакуации, акустическая изоляция в соответствии с современным уровнем техники." },
      { icon: FileCheck, no: "06", title: "Согласование и приемка", text: "Процесс получения разрешений, доказательства статической и противопожарной безопасности, окончательные приемки." },
    ],
    types_title: "Типы.",
    types: [
      { title: "Квартира в старом здании", text: "Санация и модернизация отдельных квартир — планировка, ванные комнаты, полы, окна, инженерные системы." },
      { title: "Доходный дом / многоквартирный дом", text: "Комплексная санация исторической субстанции с возможностью надстройки." },
      { title: "Частный дом", text: "Энергетическая санация, усиление оболочки здания, адаптация планировки." },
      { title: "Многофункциональное использование", text: "Перепрофилирование площадей на первом этаже, улучшение входных групп и зон доступа." },
    ],

    ref_eyebrow: "Референс-проект",
    ref_title: "Фермерский дом Зальцбург.",
    ref_body: "Бережная санация исторического фермерского дома в окрестностях Зальцбурга. Сохранение характерной для местности субстанции, энергетическая санация оболочки здания, обновление инженерных систем. Снова пригоден для постоянного использования — с характером и низким уровнем эксплуатационных расходов.",
    ref_cta: "Посмотреть проект",

    cta_title: "Проект санации в уме?",
    cta_body: "Мы оцениваем субстанцию, потенциал и экономическую эффективность на первой, ни к чему не обязывающей встрече.",
    cta_btn: "Запросить консультацию",
  },
  uk: {
    eyebrow: "Послуги · 02",
    h1a: "Санація та",
    h1b_em: "ревіталізація.",
    lede: "Дбайливе поводження з існуючими об'єктами. Ми зберігаємо субстанцію, посилюємо несучі конструкції та оболонку, а також повертаємо квартири, будинки та нерухомість до стану, придатного для довгострокового використання.",
    intro_title: "Існуюча забудова як завдання.",
    intro_body: "Санація вимагає чесної інвентаризації існуючих умов: несучих конструкцій, будівельної фізики, інженерних систем, протипожежного захисту, шкідливих речовин. На цій основі ми розробляємо реалістичну концепцію санації — з чітким втручанням там, де це необхідно, та стриманістю там, де існуючі елементи мають бути збережені.",
    scope_title: "Обсяг послуг.",
    items: [
      { icon: Building, no: "01", title: "Оцінка існуючого стану", text: "Перевірка несучих конструкцій, перевірка будівельної фізики, вимірювання шкідливих речовин та вологості, статус інженерних систем." },
      { icon: Ruler, no: "02", title: "Концепція санації", text: "Варіантне дослідження, кошторис, стратегія погодження, графік." },
      { icon: Layers, no: "03", title: "Несучі конструкції та оболонка", text: "Статичне посилення, санація фасадів, концепція термічних мостів, вікна та дах." },
      { icon: Wrench, no: "04", title: "Інженерні системи", text: "Оновлення систем опалення / вентиляції / сантехніки / електротехніки спільно з ECO Building Technik." },
      { icon: Shield, no: "05", title: "Протипожежний та звукоізоляційний захист", text: "Пожежні відсіки, шляхи евакуації, акустичне розділення відповідно до сучасного стану техніки." },
      { icon: FileCheck, no: "06", title: "Погодження та приймання", text: "Процес отримання дозволів, статичні та протипожежні сертифікати, остаточні приймання." },
    ],
    types_title: "Типи.",
    types: [
      { title: "Квартира у старому будинку", text: "Санація та оновлення окремих квартир — планування, ванні кімнати, підлоги, вікна, інженерні системи." },
      { title: "Прибутковий будинок / багатоквартирний будинок", text: "Комплексна санація історичної субстанції з можливим добудовою поверхів." },
      { title: "Приватний будинок", text: "Енергетична санація, посилення оболонки, адаптація планування." },
      { title: "Змішане використання", text: "Перепрофілювання площ першого поверху, покращення вхідних та комунікаційних зон." },
    ],

    ref_eyebrow: "Референс-проект",
    ref_title: "Фермерський будинок Зальцбург.",
    ref_body: "Дбайлива санація історичного фермерського будинку в околицях Зальцбурга. Збереження типової для місцевості субстанції, енергетична санація оболонки, оновлення інженерних систем. Знову придатний для постійного використання — з характером та низьким рівнем експлуатаційних витрат.",
    ref_cta: "Переглянути проект",

    cta_title: "Проект санації в голові?",
    cta_body: "Ми оцінюємо субстанцію, потенціал та економічну ефективність під час першої, ні до чого не зобов'язуючої розмови.",
    cta_btn: "Запитати консультацію",
  },
};

export default function SanierungPage() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.de;

  return (
    <div data-testid="sanierung-page">
      <PageMeta pageKey="sanierung" />

      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center gap-4 mb-10">
          <span className="w-12 h-px bg-white/55" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-white/65">{c.eyebrow}</span>
        </div>
        <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.95]">
          {c.h1a}
          <br />
          <span className="italic font-extralight">{c.h1b_em}</span>
        </h1>
        <p className="mt-10 max-w-2xl text-base md:text-lg text-white/80 leading-[1.65] font-light">{c.lede}</p>
      </section>

      {/* Intro */}
      <section className="py-20 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-10 lg:gap-20 border-t border-white/10">
        <div className="md:col-span-5 min-w-0">
          <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-5">01</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {c.intro_title}
          </h2>
        </div>
        <div className="md:col-span-7 md:pt-8 text-white/80 text-base md:text-lg leading-[1.7] font-light">
          <p>{c.intro_body}</p>
        </div>
      </section>

      {/* Scope */}
      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">02</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {c.scope_title}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {c.items.map((it) => (
            <div key={it.no} className="bg-[#051630] p-8 md:p-10">
              <div className="flex items-baseline justify-between mb-5">
                <it.icon size={22} strokeWidth={1.2} className="text-white/70" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">{it.no}</span>
              </div>
              <h3 className="font-display text-lg uppercase tracking-tight mb-3">{it.title}</h3>
              <p className="text-sm text-white/70 leading-[1.7] font-light">{it.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Types */}
      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">03</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {c.types_title}
        </h2>
        <div className="grid sm:grid-cols-2 gap-px bg-white/10">
          {c.types.map((t) => (
            <div key={t.title} className="bg-[#051630] p-8 md:p-10">
              <h3 className="font-display text-xl uppercase tracking-tight mb-3">{t.title}</h3>
              <p className="text-sm text-white/70 leading-[1.7] font-light">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reference project: Bauernhaus Salzburg */}
      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10 grid md:grid-cols-12 gap-10 lg:gap-16">
        <Link
          to="/projekte/bauernhaus-salzburg"
          data-testid="sanierung-reference-link"
          className="md:col-span-7 group relative overflow-hidden block aspect-[4/3] md:aspect-[3/2]"
        >
          <img
            src="/images/bauernhaus-salzburg-cover.jpg"
            alt="Bauernhaus Salzburg — Sanierungs-Referenzprojekt"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
            loading="lazy"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, rgba(5,22,48,0.6), rgba(5,22,48,0.15) 50%, rgba(5,22,48,0))" }}
          />
        </Link>
        <div className="md:col-span-5 flex flex-col justify-end">
          <p className="text-[11px] tracking-[0.3em] uppercase text-white/55 mb-4">04 · {c.ref_eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-6 break-words hyphens-auto">
            {c.ref_title}
          </h2>
          <p className="text-white/75 leading-[1.7] font-light mb-8">{c.ref_body}</p>
          <Link
            to="/projekte/bauernhaus-salzburg"
            data-testid="sanierung-reference-cta"
            className="group inline-flex items-center gap-4 self-start border-b border-white/40 hover:border-white pb-2 text-[11px] tracking-[0.25em] uppercase text-white/80 hover:text-white transition-colors"
          >
            <span>{c.ref_cta}</span>
            <ArrowRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 min-w-0">
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {c.cta_title}
          </h2>
          <p className="mt-6 max-w-xl text-white/75 leading-[1.7] font-light">{c.cta_body}</p>
        </div>
        <div className="md:col-span-5 flex md:justify-end md:items-end">
          <Link
            to="/kontakt"
            data-testid="sanierung-cta"
            className="group inline-flex items-center gap-6 bg-white text-[#051630] font-semibold tracking-[0.2em] uppercase text-[11px] px-8 py-5 border border-white hover:bg-transparent hover:text-white transition-colors"
          >
            <span>{c.cta_btn}</span>
            <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
